import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { MapPin, Search, Phone, Globe, Crown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const defaultIcon = new L.Icon.Default();

const premierIcon = L.divIcon({
  className: '',
  html: '<div style="background:#f59e0b;width:22px;height:22px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4)"></div>',
  iconSize: [22, 22],
  iconAnchor: [11, 22],
  popupAnchor: [0, -20],
});

const centerIcon = L.divIcon({
  className: '',
  html: '<div style="background:#2563eb;width:16px;height:16px;border-radius:50%;border:3px solid #fff;box-shadow:0 0 0 4px rgba(37,99,235,.3)"></div>',
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

const Recenter = ({ center, radiusMiles }) => {
  const map = useMap();
  useEffect(() => {
    if (!center) return;
    const zoom = radiusMiles <= 25 ? 10 : radiusMiles <= 50 ? 9 : radiusMiles <= 100 ? 8 : 7;
    map.setView([center.lat, center.lng], zoom);
  }, [center, radiusMiles, map]);
  return null;
};

export const NearMeMap = () => {
  const [zip, setZip] = useState('');
  const [radius, setRadius] = useState(50);
  const [specialty, setSpecialty] = useState('');
  const [specialties, setSpecialties] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/auctioneers/specialties/list`)
      .then((r) => r.json())
      .then((d) => setSpecialties(d.specialties || []))
      .catch(() => {});
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!zip.trim()) {
      setError('Please enter a ZIP code.');
      return;
    }
    setLoading(true);
    setError('');
    setSearched(true);
    try {
      const params = new URLSearchParams({ zip_code: zip.trim(), radius_miles: radius });
      if (specialty) params.append('specialty', specialty);
      const res = await fetch(`${API}/api/auctioneers/near?${params.toString()}`);
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail || 'Search failed');
      setData(body);
    } catch (err) {
      setError(err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const auctioneers = data?.auctioneers || [];
  const center = data?.center;

  const defaultCenter = useMemo(() => center || { lat: 39.8283, lng: -98.5795 }, [center]);

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50" data-testid="near-me-page">
        <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl sm:text-5xl font-bold mb-3">Auctioneers Near Me</h1>
            <p className="text-base md:text-lg text-blue-100 max-w-2xl mx-auto">
              Enter your ZIP code to see licensed auctioneers around you on the map — sorted by distance.
            </p>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
          <form onSubmit={handleSearch} className="bg-white rounded-xl shadow-xl p-5" data-testid="near-search-form">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                  placeholder="e.g. 43004"
                  data-testid="near-zip-input"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Radius</label>
                <select
                  value={radius}
                  onChange={(e) => setRadius(Number(e.target.value))}
                  data-testid="near-radius-select"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value={25}>25 miles</option>
                  <option value={50}>50 miles</option>
                  <option value={100}>100 miles</option>
                  <option value={200}>200 miles</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Specialty</label>
                <select
                  value={specialty}
                  onChange={(e) => setSpecialty(e.target.value)}
                  data-testid="near-specialty-select"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Specialties</option>
                  {specialties.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="near-search-btn"
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition disabled:opacity-50"
                >
                  <Search className="w-5 h-5" /> {loading ? 'Searching…' : 'Search'}
                </button>
              </div>
            </div>
            {error && (
              <p className="mt-3 text-sm text-red-600" data-testid="near-error">{error}</p>
            )}
          </form>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Map */}
            <div className="lg:col-span-3">
              <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm" style={{ height: '520px' }} data-testid="near-map">
                <MapContainer center={[defaultCenter.lat, defaultCenter.lng]} zoom={center ? 9 : 4} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {center && <Recenter center={center} radiusMiles={data?.radius_miles || radius} />}
                  {center && (
                    <>
                      <Marker position={[center.lat, center.lng]} icon={centerIcon}>
                        <Popup>Your location (ZIP {zip})</Popup>
                      </Marker>
                      <Circle
                        center={[center.lat, center.lng]}
                        radius={(data?.radius_miles || radius) * 1609.34}
                        pathOptions={{ color: '#2563eb', fillColor: '#3b82f6', fillOpacity: 0.06, weight: 1 }}
                      />
                    </>
                  )}
                  {auctioneers.map((a) => (
                    <Marker
                      key={a.id}
                      position={[a.latitude, a.longitude]}
                      icon={a.premier_active ? premierIcon : defaultIcon}
                    >
                      <Popup>
                        <div className="text-sm">
                          <div className="font-semibold text-gray-900">{a.name}</div>
                          <div className="text-gray-600">{a.city}, {a.state} · {a.distance_miles} mi</div>
                          {a.premier_active && (
                            <div className="text-amber-600 font-medium flex items-center gap-1 mt-1"><Crown className="w-3 h-3" /> Premier</div>
                          )}
                          {a.slug && (
                            <Link to={`/auctioneer/${a.slug}`} className="text-blue-600 hover:underline block mt-1">View profile →</Link>
                          )}
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>
              <p className="text-xs text-gray-500 mt-2 flex items-center gap-3">
                <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-blue-600" /> You</span>
                <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 bg-amber-500" style={{ borderRadius: '50% 50% 50% 0', transform: 'rotate(-45deg)' }} /> Premier</span>
                <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3 text-gray-500" /> Auctioneer</span>
              </p>
            </div>

            {/* Results list */}
            <div className="lg:col-span-2">
              {searched && !loading && (
                <h2 className="text-lg md:text-lg font-bold text-gray-900 mb-3" data-testid="near-results-count">
                  {data?.total || 0} auctioneer{(data?.total || 0) !== 1 ? 's' : ''} within {data?.radius_miles || radius} miles
                </h2>
              )}
              <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1" data-testid="near-results">
                {loading ? (
                  <div className="text-center py-12 text-gray-500">Searching…</div>
                ) : !searched ? (
                  <div className="text-center py-12 text-gray-500 bg-white rounded-xl border border-gray-200">
                    <MapPin className="w-8 h-8 mx-auto text-gray-300 mb-2" />
                    Enter a ZIP code to find auctioneers near you.
                  </div>
                ) : auctioneers.length === 0 ? (
                  <div className="text-center py-12 text-gray-500 bg-white rounded-xl border border-gray-200">
                    No auctioneers found in this area. Try a larger radius.
                  </div>
                ) : (
                  auctioneers.map((a) => (
                    <div
                      key={a.id}
                      data-testid="near-result-card"
                      className={`bg-white rounded-xl border p-4 hover:shadow-md transition ${a.premier_active ? 'border-amber-300' : 'border-gray-200'}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="font-semibold text-gray-900 flex items-center gap-2">
                            {a.name}
                            {a.premier_active && <Crown className="w-4 h-4 text-amber-500" />}
                          </div>
                          <div className="text-sm text-gray-500 flex items-center gap-1 mt-0.5">
                            <MapPin className="w-3.5 h-3.5" /> {a.city}, {a.state}
                          </div>
                        </div>
                        <span className="text-sm font-medium text-blue-600 whitespace-nowrap">{a.distance_miles} mi</span>
                      </div>
                      {a.specialties?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {a.specialties.slice(0, 3).map((s, i) => (
                            <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">{s}</span>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center gap-3 mt-3">
                        {a.slug && (
                          <Link to={`/auctioneer/${a.slug}`} className="text-sm font-medium text-blue-600 hover:underline">View profile</Link>
                        )}
                        {a.phone && (
                          <a href={`tel:${a.phone}`} className="text-sm text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> Call</a>
                        )}
                        {a.website && (
                          <a href={a.website} target="_blank" rel="noopener noreferrer" className="text-sm text-gray-500 hover:text-gray-700 inline-flex items-center gap-1"><Globe className="w-3.5 h-3.5" /> Site</a>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default NearMeMap;
